import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import axiosInstance from "services/axiosInstance";
import * as S from "./styled";
//Components
import LayoutModal from "../../../LayoutModal";
import Loading from "../../../Loading";
//hooks
import apiRequest from "../../../../hooks/apiRequest";
import useLoading from '../../../../hooks/useLoading';
//utils
import { CurrencyFormat } from "utils/CurrencyFormat";
import FormatarData from "utils/FormatData";
//Interface
import { ICancel, Inputs } from "Interfaces/ICancel";
import { ICreate } from "Interfaces/ICreate";
//icons
import { FlagBanner, ReceiptX } from "@phosphor-icons/react";

export default function Update({
  selectedUrlId,
  isOpenModal,
  setModalClose,
  refreshFrames,
  setToastData,
  setToastOpen
}: ICreate) {
  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>();

  const { isLoading, startLoading, stopLoading } = useLoading();
  const [dataSale, setDataSale] = useState<ICancel | null>(null);

  const reasonsForms = [
    { reason: 'Cliente desistiu da compra', id: 'reason-1' },
    { reason: 'Produto indisponível', id: 'reason-2' },
    { reason: 'Erro no preço/desconto', id: 'reason-3' },
    { reason: 'Problema no pagamento', id: 'reason-4' },
    { reason: 'Erro do operador', id: 'reason-5' },
    { reason: 'Solicitação do cliente', id: 'reason-6' },
    { reason: 'Outros', id: 'reason-7' },
  ];

  const flagsForms = [
    {
      title: 'Bandeira Vermelha',
      flag: 'red',
      reason: 'Cliente apresentou problemas graves (Evitar futuras vendas).',
      status: 'error',
    }, {
      title: 'Bandeira Laranja',
      flag: 'orange',
      reason: 'Cliente apresentou alguns problemas (Requer atenção).',
      status: 'warning',
    }, {
      title: 'Sem Restrição',
      flag: 'green',
      reason: 'Cliente não apresentou problemas durante a transação.',
      status: 'sucess',
    },
  ];

  const fetchData = async () => {
    startLoading();
    try {
      const response = await axiosInstance.get(`cancellation/${selectedUrlId}`);
      const addressData = response.data;
      setDataSale(addressData);
      for (const key in addressData) {
        setValue(key as keyof Inputs, addressData[key]);
      }
      setValue("flag", addressData.sale.customer.flag);
    } catch (error) {
      console.log("Error: ", error);
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    if (isOpenModal && selectedUrlId !== null) {
      fetchData();
    }
  }, [isOpenModal, selectedUrlId]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    startLoading();
    try {
      const payload = {
        ...data,
        sale_id: dataSale ? dataSale?.sale_id : null,
      }

      const response = await apiRequest(`cancellation/${selectedUrlId}`, 'PATCH', payload);
      if (response.status === 200) {
        setToastData({
          status: response.status,
          message: 'Cadastrado com sucesso!',
        });

        setToastOpen(true);
        refreshFrames();
        onClose();

      }
    } catch (error: any) {
      setToastData({
        status: error?.response?.status || 500,
        message: error.message || 'Erro inesperado',
      });

      setToastOpen(true);
    } finally {
      stopLoading();
    }
  };

  const onClose = () => {
    setDataSale(null);
    setModalClose();
    reset();
  };

  if (isOpenModal) {
    return (
      <LayoutModal
        statusLoad={isLoading}
        title="Atualizar Cancelamento"
        subTitle="Atualize as informações do cancelamento em sua base de dados."
        setModalClose={onClose}
        submiteModal={handleSubmit(onSubmit)}

        nameIconMinimize={'Atualizar Cancelamento'}
      >
        {isLoading ? (
          <Loading />
        ) : (
          <S.Container>
            {
              dataSale &&
              <S.ContentInfor>
                <S.Icons>
                  <ReceiptX size={24} />
                </S.Icons>
                <div>
                  <S.InforDescription className="title">Número da venda #{dataSale.sale?.number_ata || '-'}</S.InforDescription>
                  <S.InforDescription>Data: {FormatarData(dataSale.sale?.date_sale, 'default') || '-'}</S.InforDescription>
                  <S.InforDescription>Total: {CurrencyFormat(parseFloat(dataSale.sale?.total_amount || '0'))}</S.InforDescription>
                  <S.InforDescription>Cliente: {dataSale.sale?.customer?.full_name || '-'}</S.InforDescription>
                  <S.InforDescription>Vendedor: {dataSale.sale?.user?.first_name || '-'}</S.InforDescription>
                </div>
              </S.ContentInfor>
            }

            <S.Section>
              <S.Title>
                Motivo do Cancelamento
                <span>*</span>
              </S.Title>
              {
                reasonsForms.map((reason, index) =>
                  <S.ContentInput>
                    <S.Input
                      {...register('reason', { required: true })}
                      type="radio"
                      id={`reason-${index}`}
                      name="reason"
                      value={reason.reason}
                    />
                    <S.Label
                      htmlFor={`reason-${index}`}
                      className="infor"
                    >
                      {reason.reason}
                    </S.Label>
                  </S.ContentInput>
                )
              }
              {errors.flag && <S.InforErro>Campo Obrigatório</S.InforErro>}
            </S.Section>

            <S.FormInput>
              <S.SectionHighlight>
                <S.Title>
                  Bandeira do Cliente <span>*</span>
                </S.Title>

                {flagsForms.map((flag, index) => (
                  <S.ContentHighlight key={index}>
                    <S.Input
                      {...register("flag", { required: true })}
                      type="radio"
                      id={`flag-${index}`}
                      name="flag"
                      value={flag.flag}
                    />
                    <S.Label htmlFor={`flag-${index}`} className={flag.status}>
                      <FlagBanner size={14} weight="fill" />
                      {flag.title}
                    </S.Label>
                    <S.InforDescription>{flag.reason}</S.InforDescription>
                  </S.ContentHighlight>
                ))}
              </S.SectionHighlight>

              {errors.flag && <S.InforErro>Campo Obrigatório</S.InforErro>}
            </S.FormInput>


            <S.FormInput>
              <S.Label>Observações adicionais</S.Label>
              <S.TextArea
                {...register('observation')}
                placeholder="Descreva detalhes adicionais sobre o cancelamento..."
                rows={2}
              />
            </S.FormInput>
          </S.Container>
        )}
      </LayoutModal >
    );
  }
  return null;
}
